/**
 * Tracking público del sitio (tenant-web).
 * POST /public/tenant/:tenantUuid/tracking-event — sin JWT.
 * @see core docs/frontend/informe-stats-tracking.md §3
 */

import { getApiBaseUrl } from './tenantConfig'

const SESSION_STORAGE_KEY = 'workit.tracking.sessionId'
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'
const MAX_URL = 500
const MAX_UA = 500
const MAX_REFERRER = 500

/** @param {unknown} value */
export function isTrackingUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim())
}

/**
 * Resuelve tenantUuid (36 chars) desde config / propuesta / env.
 * @param {...unknown} candidates
 * @returns {string | null}
 */
export function resolveTenantUuid(...candidates) {
  for (const c of candidates) {
    if (c == null) continue
    if (typeof c === 'string' && isTrackingUuid(c)) return c.trim()
    if (typeof c === 'object') {
      const obj = /** @type {Record<string, unknown>} */ (c)
      const nested = [
        obj.tenantUuid,
        obj.tenant,
        obj?.tenant && typeof obj.tenant === 'object'
          ? /** @type {Record<string, unknown>} */ (obj.tenant).uuid
          : null,
        obj?.website && typeof obj.website === 'object'
          ? /** @type {Record<string, unknown>} */ (obj.website).tenantUuid
          : null,
        obj?.publicInfo && typeof obj.publicInfo === 'object'
          ? (() => {
              const pi = /** @type {Record<string, unknown>} */ (obj.publicInfo)
              if (typeof pi.tenantUuid === 'string') return pi.tenantUuid
              if (pi.tenant && typeof pi.tenant === 'object') {
                return /** @type {Record<string, unknown>} */ (pi.tenant).uuid
              }
              return null
            })()
          : null,
      ]
      for (const n of nested) {
        if (typeof n === 'string' && isTrackingUuid(n)) return n.trim()
      }
    }
  }
  const fromEnv = String(import.meta.env.VITE_TENANT_ID || '').trim()
  return isTrackingUuid(fromEnv) ? fromEnv : null
}

function clip(value, max) {
  const s = String(value ?? '')
  return s.length <= max ? s : s.slice(0, max)
}

function createAnonymousSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `wk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** sessionId anónimo estable en localStorage (no rotar a medianoche local). */
export function getTrackingSessionId() {
  if (typeof window === 'undefined') return createAnonymousSessionId()
  try {
    const existing = window.localStorage.getItem(SESSION_STORAGE_KEY)?.trim()
    if (existing && existing.length >= 2 && existing.length <= 100) return existing
    const next = createAnonymousSessionId()
    window.localStorage.setItem(SESSION_STORAGE_KEY, next)
    return next
  } catch {
    return createAnonymousSessionId()
  }
}

function buildClientContext() {
  const url =
    typeof window !== 'undefined' && window.location?.href
      ? clip(window.location.href, MAX_URL)
      : '/'
  const rawReferrer =
    typeof document !== 'undefined' && document.referrer ? String(document.referrer).trim() : ''
  const referrer = clip(rawReferrer || 'direct', MAX_REFERRER)
  const rawUa =
    typeof navigator !== 'undefined' && navigator.userAgent
      ? String(navigator.userAgent)
      : 'unknown'
  const userAgent = clip(rawUa || 'unknown', MAX_UA)
  return { url, referrer, userAgent }
}

function isDuplicateSessionError(status, body) {
  if (status !== 400) return false
  const type =
    (typeof body?.type === 'string' && body.type) ||
    (typeof body?.metadata?.type === 'string' && body.metadata.type) ||
    (typeof body?.error?.type === 'string' && body.error.type) ||
    ''
  if (type === 'tracking.event.session.duplicate') return true
  const msg = typeof body?.message === 'string' ? body.message.toLowerCase() : ''
  return msg.includes('session.duplicate') || msg.includes('ya fue registrado')
}

/**
 * Fire-and-forget. No lanza; duplicate → silencio.
 * @param {{
 *   tenantUuid: string,
 *   type: string,
 *   entityType: string,
 *   entityUuid?: string | null,
 *   metadata?: Record<string, unknown>,
 * }} params
 */
export function trackEvent({ tenantUuid, type, entityType, entityUuid = null, metadata = {} }) {
  if (USE_MOCK) return
  if (typeof window === 'undefined') return

  const tid = typeof tenantUuid === 'string' ? tenantUuid.trim() : ''
  if (!isTrackingUuid(tid)) return
  if (!type || !entityType) return

  const apiBase = getApiBaseUrl()
  if (!apiBase) return

  const { url, referrer, userAgent } = buildClientContext()
  const body = {
    tenantUuid: tid,
    type: String(type),
    entityType: String(entityType),
    entityUuid: entityUuid && isTrackingUuid(entityUuid) ? entityUuid.trim() : null,
    sessionId: getTrackingSessionId(),
    url,
    referrer,
    userAgent,
    metadataVersion: 1,
    metadata: metadata && typeof metadata === 'object' ? metadata : {},
  }

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': 'es',
  }
  const apiKey = String(import.meta.env.VITE_TRACKING_API_KEY || '').trim()
  if (apiKey) headers['X-Tracking-Api-Key'] = apiKey

  const endpoint = `${apiBase}/public/tenant/${encodeURIComponent(tid)}/tracking-event`

  void fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    keepalive: true,
  })
    .then(async (res) => {
      if (res.ok || res.status === 201) return
      let parsed = null
      try {
        parsed = await res.json()
      } catch {
        parsed = null
      }
      if (isDuplicateSessionError(res.status, parsed)) return
      if (import.meta.env.DEV) {
        console.warn('[tracking]', res.status, parsed?.type || parsed?.message || res.statusText)
      }
    })
    .catch((err) => {
      if (import.meta.env.DEV) {
        console.warn('[tracking] network', err)
      }
    })
}

export function trackWebsiteView(tenantUuid) {
  trackEvent({
    tenantUuid,
    type: 'WEBSITE_VIEW',
    entityType: 'WEBSITE',
    entityUuid: null,
    metadata: {},
  })
}

export function trackVehicleView(tenantUuid, vehicleUuid) {
  if (!isTrackingUuid(vehicleUuid)) return
  trackEvent({
    tenantUuid,
    type: 'WEBSITE_VEHICLE_VIEW',
    entityType: 'VEHICLE',
    entityUuid: vehicleUuid,
    metadata: {},
  })
}

export function trackVehicleContactClick(tenantUuid, vehicleUuid) {
  if (!isTrackingUuid(vehicleUuid)) return
  trackEvent({
    tenantUuid,
    type: 'WEBSITE_VEHICLE_CONTACT_CLICK',
    entityType: 'VEHICLE',
    entityUuid: vehicleUuid,
    metadata: {},
  })
}

export function trackProposalView(tenantUuid, proposalUuid) {
  if (!isTrackingUuid(proposalUuid)) return
  trackEvent({
    tenantUuid,
    type: 'PROPOSAL_VIEW',
    entityType: 'PROPOSAL',
    entityUuid: proposalUuid,
    metadata: {},
  })
}

export function trackProposalVehicleView(tenantUuid, proposalUuid, vehicleUuid) {
  if (!isTrackingUuid(proposalUuid) || !isTrackingUuid(vehicleUuid)) return
  trackEvent({
    tenantUuid,
    type: 'PROPOSAL_VEHICLE_VIEW',
    entityType: 'PROPOSAL',
    entityUuid: proposalUuid,
    metadata: { vehicleUuid: String(vehicleUuid).trim() },
  })
}
